import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '@/api/ApiContext';
import type { JournalEntryWriteRequest } from '@/api/handlers/resources/journal/requests';
import type { JournalEntryWriteResponse } from '@/api/handlers/resources/journal/responses';
import type { ApiResponse } from '@/api/handlers/responses';
import { JournalQueryKeys } from './journalQueryKeys';

/** `etag` selects create vs update — see `IJournalService.saveDraft`. */
export type SaveJournalDraftInput = {
    body: JournalEntryWriteRequest;
    /**
     * The last etag read from a `GET`. Absent on a first save (a create).
     *
     * It must come from a read, not from a previous write response: the server
     * rejects a write-response etag on the next write. `JournalService` handles
     * that by re-reading after a create, so callers only ever hold good ones.
     */
    etag?: string;
};

/**
 * Save (create or update) a draft journal entry. Create is idempotent on
 * `body.id`, so a retried create returns the existing entry rather than a
 * duplicate — but note it is **first-write-wins**, not an upsert: replaying the
 * id with changed content returns the original unchanged. Editing therefore has
 * to go through the update path, which is what `etag` selects.
 *
 * The envelope never rejects — callers branch on `result.success`. On success we
 * prime the entry cache and invalidate the resource so a future list refetches.
 */
export const useSaveJournalDraftMutation = () => {
    const { apiClient } = useApi();
    const queryClient = useQueryClient();

    return useMutation<
        ApiResponse<JournalEntryWriteResponse>,
        Error,
        SaveJournalDraftInput
    >({
        mutationFn: ({ body, etag }) => apiClient.journal.saveDraft(body, etag),
        onSuccess: (result) => {
            if (result.success) {
                queryClient.setQueryData(
                    JournalQueryKeys.entry(result.response.id),
                    result,
                );
                queryClient.invalidateQueries({
                    queryKey: JournalQueryKeys.all,
                });
            }
        },
    });
};
